'use client'

import type { MouseEvent } from 'react'

import { useAppLocale } from '@/components/i18n/locale-provider'
import { useUnlocalizedPathname } from '@/components/i18n/use-unlocalized-pathname'
import { GlobeIcon } from '@/components/icons/globe-icon'
import {
  ENABLED_LOCALES,
  LOCALE_NATIVE_NAMES,
  type Locale,
  SET_LOCALE_PARAM,
  toLanguageTag,
} from '@/lib/i18n/config'
import { localizePath } from '@/lib/i18n/routing'

/** Opens `href` with the choice attached; the proxy stores it and drops it. */
function choose(event: MouseEvent<HTMLAnchorElement>, locale: Locale) {
  event.preventDefault()
  const url = new URL(event.currentTarget.href)
  url.searchParams.set(SET_LOCALE_PARAM, locale)
  window.location.assign(url)
}

/**
 * The marketing footer's language menu: this page in every enabled
 * language, as crawlable links (`hreflang`), each name in its own language.
 * Following one stores the choice, so unprefixed URLs keep opening in it.
 */
export function LanguageSwitcher({ label }: { label: string }) {
  const current = useAppLocale()
  const pathname = useUnlocalizedPathname() ?? '/'
  return (
    <details className="relative">
      <summary
        aria-label={label}
        className="flex cursor-pointer list-none items-center gap-1.5 text-muted-foreground text-xs transition-colors hover:text-foreground [&::-webkit-details-marker]:hidden"
      >
        <GlobeIcon aria-hidden className="size-3.5" />
        {LOCALE_NATIVE_NAMES[current]}
      </summary>
      <ul className="absolute bottom-full left-0 z-10 mb-2 grid max-h-72 min-w-44 overflow-y-auto rounded-lg border border-border bg-background p-1 shadow-lg">
        {ENABLED_LOCALES.map((locale) => {
          const tag = toLanguageTag(locale)
          return (
            <li key={locale}>
              <a
                href={localizePath(locale, pathname)}
                // biome-ignore lint/suspicious/noUnknownAttribute: React's anchor prop is `hrefLang` (AnchorHTMLAttributes); the rule only knows the HTML spelling
                hrefLang={tag}
                lang={tag}
                aria-current={locale === current ? 'true' : undefined}
                onClick={(event) => choose(event, locale)}
                className="block rounded-md px-2.5 py-1.5 text-muted-foreground text-xs transition-colors hover:bg-muted hover:text-foreground aria-[current=true]:font-medium aria-[current=true]:text-foreground"
              >
                {LOCALE_NATIVE_NAMES[locale]}
              </a>
            </li>
          )
        })}
      </ul>
    </details>
  )
}
